/**
 * ============================================================================
 * TRANSIENT: Appointment Config Page (appointment-config-page)
 * Type: poc
 * Amplitude key: none
 * End date: 2026-12-31
 * PR: #TODO_AFTER_PR_CREATION_appointment-config-page
 * ============================================================================
 */

import { Box, Stack, Switch, Typography, paletteColors } from '@evaneos/b2b-ui';

const DURATION_OPTIONS = [
    { value: 15, label: '15 min' },
    { value: 30, label: '30 min' },
    { value: 45, label: '45 min' },
    { value: 60, label: '60 min' },
] as const;

type Props = {
    slotDurationInMinutes: 15 | 30 | 45 | 60;
    travelerReminder: boolean;
    onDurationChange: (duration: 15 | 30 | 45 | 60) => void;
    onTravelerReminderChange: (travelerReminder: boolean) => void;
};

export const AppointmentParamsPartial = ({
    slotDurationInMinutes,
    travelerReminder,
    onDurationChange,
    onTravelerReminderChange,
}: Props): JSX.Element => (
    <Stack data-spec-id="agenda-section-params" spacing={3}>
        <Box>
            <Typography variant="h3" sx={{ fontWeight: 700, fontSize: 20, mb: 0.5 }}>
                {/* @todo translate: agenda.params.title */}
                Paramètres des rendez-vous
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {/* @todo translate: agenda.params.description */}
                Configurez vos créneaux de rendez-vous voyageur.
            </Typography>
        </Box>

        {/* Durée */}
        <Box>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                {/* @todo translate: agenda.params.duration.label */}
                Durée du rendez-vous
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {DURATION_OPTIONS.map((opt) => {
                    const selected = slotDurationInMinutes === opt.value;
                    return (
                        <Box
                            key={opt.value}
                            data-spec-id={`duration-card-${opt.value}`}
                            component="button"
                            className={selected ? 'duration-card--selected' : ''}
                            onClick={() => onDurationChange(opt.value)}
                            sx={{
                                px: 2,
                                py: 1,
                                border: '1px solid',
                                borderColor: selected ? 'primary.main' : 'divider',
                                borderRadius: 1,
                                cursor: 'pointer',
                                bgcolor: selected ? 'primary.main' : 'background.paper',
                                color: selected ? 'primary.contrastText' : 'text.primary',
                                fontSize: 14,
                                fontWeight: selected ? 700 : 400,
                                '&:hover': { borderColor: 'primary.main' },
                            }}
                        >
                            {opt.label}
                        </Box>
                    );
                })}
            </Box>
        </Box>

        {/* Rappel */}
        <Stack spacing={2}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2,
                }}
            >
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {/* @todo translate: agenda.params.rappel.label */}
                        Rappel automatique voyageur
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 350 }}>
                        {/* @todo translate: agenda.params.rappel.description */}
                        Un message de rappel sera envoyé au voyageur 24h avant l&apos;appel depuis
                        Request Manager.
                    </Typography>
                </Box>
                <Switch
                    data-spec-id="agenda-rappel-toggle"
                    checked={travelerReminder}
                    onChange={(e) => onTravelerReminderChange(e.target.checked)}
                    color="primary"
                    size="medium"
                    sx={{ m: 0, flexShrink: 0 }}
                />
            </Box>

            {travelerReminder && (
                <Box data-spec-id="agenda-rappel-preview">
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                        {/* @todo translate: agenda.params.rappel.preview_label */}
                        Voici le message qui sera envoyé :
                    </Typography>
                    <Box
                        sx={{
                            bgcolor: paletteColors.neutral[100],
                            p: 2,
                            borderRadius: 1,
                        }}
                    >
                        <Typography variant="body2" sx={{ fontStyle: 'italic', lineHeight: 1.6 }}>
                            {/* @todo translate: agenda.params.rappel.message */}
                            &quot;Bonjour {'{prenom_voyageur}'}, nous vous rappelons que nous avons
                            rendez-vous ensemble le {'{date}'} à {'{heure}'}.<br />
                            Merci et à bientôt.&quot;
                        </Typography>
                    </Box>
                </Box>
            )}
        </Stack>
    </Stack>
);
